import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Check, X, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

export default function AutoJobsAdmin({ onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/pending-jobs")
      .then((r) => { setData(r.data); setSel(new Set()); })
      .catch(() => toast.error("Failed to load queue"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const jobs = data?.jobs || [];
  const bySource = data?.by_source || {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) =>
      (sourceFilter === "all" || j.source === sourceFilter) &&
      (!q || (j.title || "").toLowerCase().includes(q) || (j.company || "").toLowerCase().includes(q))
    );
  }, [jobs, sourceFilter, search]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((j) => sel.has(j.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((s) => {
    const n = new Set(s);
    if (allVisibleSelected) filtered.forEach((j) => n.delete(j.id));
    else filtered.forEach((j) => n.add(j.id));
    return n;
  });

  const act = async (kind, payload, label) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/pending-jobs/${kind}`, payload);
      toast.success(`${kind === "approve" ? "Published" : "Rejected"} ${res[kind === "approve" ? "approved" : "rejected"]} ${label}`);
      load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selectedIds = Array.from(sel);

  return (
    <div data-testid="admin-autojobs">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-lg">Auto-discovered Jobs — Review Queue</h3>
          <p className="text-sm text-[#94A3B8]" data-testid="autojobs-count">
            {data?.total ?? 0} awaiting review · approve to publish to the live board
          </p>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="autojobs-source-summary">
            {Object.entries(bySource).map(([src, n]) => (
              <span key={src} className="text-xs font-mono-tech px-2 py-1 rounded bg-white/5 border border-white/10 text-[#cbd5e1]">
                {src}: <span className="text-[#00F0FF]">{n}</span>
              </span>
            ))}
          </div>
        </div>
        <button onClick={load} disabled={loading} data-testid="autojobs-refresh"
          className="flex items-center gap-1.5 text-sm border border-white/10 px-3 py-2 rounded-md hover:bg-white/5 transition">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title or company…"
          data-testid="autojobs-search"
          className="flex-1 min-w-[200px] bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="autojobs-source-filter"
          className="bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none">
          <option value="all">All sources</option>
          {Object.keys(bySource).map((src) => <option key={src} value={src}>{src}</option>)}
        </select>
      </div>

      {/* Bulk action bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
        <label className="flex items-center gap-2 text-sm text-[#cbd5e1] cursor-pointer">
          <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="autojobs-select-all" />
          Select all {filtered.length !== jobs.length ? `(${filtered.length} shown)` : ""}
        </label>
        <span className="text-sm text-[#64748B]" data-testid="autojobs-selected-count">{selectedIds.length} selected</span>
        <div className="flex-1" />
        <button disabled={busy || !selectedIds.length} data-testid="autojobs-approve-selected"
          onClick={() => act("approve", { ids: selectedIds }, "listing(s)")}
          className="flex items-center gap-1.5 text-sm bg-[#4ade80] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition disabled:opacity-40">
          <Check className="w-4 h-4" /> Approve selected
        </button>
        <button disabled={busy || !selectedIds.length} data-testid="autojobs-reject-selected"
          onClick={() => act("reject", { ids: selectedIds }, "listing(s)")}
          className="flex items-center gap-1.5 text-sm bg-[#f87171]/90 text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition disabled:opacity-40">
          <X className="w-4 h-4" /> Reject selected
        </button>
        {sourceFilter !== "all" && (
          <button disabled={busy} data-testid="autojobs-approve-source"
            onClick={() => act("approve", { source: sourceFilter }, `${sourceFilter} listings`)}
            className="text-sm border border-[#4ade80]/40 text-[#4ade80] px-4 py-2 rounded-md hover:bg-[#4ade80]/10 transition disabled:opacity-40">
            Approve all {sourceFilter}
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-[#94A3B8] py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[#94A3B8] py-12 text-center" data-testid="autojobs-empty">
          {jobs.length === 0 ? "Queue is empty — nothing awaiting review." : "No jobs match your filter."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="autojobs-list">
          {filtered.map((j) => (
            <div key={j.id} data-testid={`autojob-row-${j.id}`}
              className="card-surface rounded-lg p-4 flex items-start gap-3">
              <input type="checkbox" checked={sel.has(j.id)} onChange={() => toggle(j.id)}
                data-testid={`autojob-check-${j.id}`} className="mt-1.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white">{j.title}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF] font-mono-tech">{j.source}</span>
                </div>
                <p className="text-sm text-[#94A3B8] mt-0.5">
                  {j.company}{j.location ? ` · ${j.location}` : ""}{j.country_flag ? ` ${j.country_flag}` : ""}
                  <span className="text-[#64748B]"> · pulled {fmtDate(j.fetched_at)}</span>
                </p>
                {j.description && (
                  <div className="mt-2">
                    <p className="text-sm text-[#cbd5e1]">
                      {expanded === j.id ? j.description : `${(j.description || "").slice(0, 140)}${(j.description || "").length > 140 ? "…" : ""}`}
                    </p>
                    {(j.description || "").length > 140 && (
                      <button onClick={() => setExpanded(expanded === j.id ? null : j.id)}
                        data-testid={`autojob-expand-${j.id}`}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-[#00F0FF] hover:underline">
                        {expanded === j.id ? <>Less <ChevronUp className="w-3 h-3" /></> : <>More <ChevronDown className="w-3 h-3" /></>}
                      </button>
                    )}
                  </div>
                )}
                <a href={j.apply_url} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#00F0FF] transition">
                  View original <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button disabled={busy} onClick={() => act("approve", { ids: [j.id] }, "listing")}
                  data-testid={`autojob-approve-${j.id}`}
                  className="text-xs bg-[#4ade80] text-[#04121a] font-semibold px-3 py-1.5 rounded hover:brightness-110 transition disabled:opacity-40">Approve</button>
                <button disabled={busy} onClick={() => act("reject", { ids: [j.id] }, "listing")}
                  data-testid={`autojob-reject-${j.id}`}
                  className="text-xs border border-[#f87171]/40 text-[#f87171] px-3 py-1.5 rounded hover:bg-[#f87171]/10 transition disabled:opacity-40">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
